import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, X, Truck, Minus, Plus, Trash, ArrowRight, CalendarCheck, MapPin, User, SignIn } from '@phosphor-icons/react';
import { useAuth } from '../../../context/AuthContext';
import { useCart } from '../../../context/CartContext';

const FloatingCart = ({
  cartItems, removeItem, updateQuantity, cartTotal, addItem,
  colors, getLink, storeSlug, deliveryInfo, products,
  floatingCartOpen, setFloatingCartOpen
}) => {
  const { user } = useAuth();
  const { taxBreakdown } = useCart();
  const FREE_DELIVERY_THRESHOLD = deliveryInfo?.free_threshold || 100;
  const deliveryProgress = Math.min(100, ((cartTotal || 0) / FREE_DELIVERY_THRESHOLD) * 100);
  const remaining = Math.max(0, FREE_DELIVERY_THRESHOLD - (cartTotal || 0));
  const DELIVERY_DAYS = deliveryInfo?.all_days || ['Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const DELIVERY_POSTCODES = deliveryInfo?.all_postcodes || [];
  const cartProductIds = (cartItems || []).map(i => i.product_id);

  const featuredSuggest = products.filter(p =>
    !cartProductIds.includes(p.id) &&
    p.stock_status !== 'outofstock' &&
    parseFloat(p.price) > 0 &&
    (p.tags?.includes('mis_en_avant_panier') || p.tags?.includes('featured') || p.is_featured)
  );
  const otherSuggest = products.filter(p =>
    !cartProductIds.includes(p.id) &&
    p.stock_status !== 'outofstock' &&
    parseFloat(p.price) > 0 &&
    !featuredSuggest.includes(p)
  );
  const suggested = [...featuredSuggest, ...otherSuggest].slice(0, 6);

  return (
    <div className="fixed right-4 bottom-6 z-50 hidden md:flex flex-col items-end" data-testid="floating-cart">
      <div className="relative">
        <span className="absolute inset-0 rounded-full animate-ping opacity-20" style={{ backgroundColor: colors.primary }} />
        <span className="absolute inset-[-4px] rounded-full animate-pulse opacity-15" style={{ backgroundColor: colors.primary }} />
        <button onClick={() => setFloatingCartOpen(prev => !prev)}
          className="relative w-[60px] h-[60px] rounded-full text-white shadow-2xl transition-all hover:scale-110 flex items-center justify-center"
          style={{ backgroundColor: colors.primary }}
          data-testid="floating-cart-toggle">
          <ShoppingCart size={24} weight="fill" />
          {(cartItems?.length || 0) > 0 && (
            <span className="absolute -top-1 -right-1 w-[22px] h-[22px] rounded-full bg-white text-[11px] font-black flex items-center justify-center shadow-md" style={{ color: colors.primary }}>
              {cartItems.length}
            </span>
          )}
        </button>
      </div>

      {floatingCartOpen && (
        <div className="absolute bottom-[70px] right-0 w-[370px] flex flex-col animate-fadeInUp" style={{ animationDuration: '0.25s', maxHeight: '80vh', filter: 'drop-shadow(0 8px 30px rgba(0,0,0,0.18))' }}>
          <div className="flex justify-center -mb-[10px] z-10">
            <div className="w-[80px] h-[28px] rounded-[3px] opacity-70" style={{ backgroundColor: colors.primary + '80', backdropFilter: 'blur(2px)', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }} />
          </div>

          <div className="relative">
            <svg viewBox="0 0 370 12" className="w-full block" preserveAspectRatio="none" style={{ height: 12 }}>
              <path d="M0,12 L0,6 Q5,0 10,6 Q15,12 20,6 Q25,0 30,6 Q35,12 40,6 Q45,0 50,6 Q55,12 60,6 Q65,0 70,6 Q75,12 80,6 Q85,0 90,6 Q95,12 100,6 Q105,0 110,6 Q115,12 120,6 Q125,0 130,6 Q135,12 140,6 Q145,0 150,6 Q155,12 160,6 Q165,0 170,6 Q175,12 180,6 Q185,0 190,6 Q195,12 200,6 Q205,0 210,6 Q215,12 220,6 Q225,0 230,6 Q235,12 240,6 Q245,0 250,6 Q255,12 260,6 Q265,0 270,6 Q275,12 280,6 Q285,0 290,6 Q295,12 300,6 Q305,0 310,6 Q315,12 320,6 Q325,0 330,6 Q335,12 340,6 Q345,0 350,6 Q355,12 360,6 Q365,0 370,6 L370,12 Z" fill="#faf8f4" />
            </svg>
          </div>

          <div className="flex flex-col overflow-hidden" style={{ backgroundColor: '#faf8f4', borderLeft: '3px solid ' + colors.primary + '40', borderRight: '1px solid #e8e4dc' }}>
            <div className="px-5 pt-4 pb-3 flex items-center justify-between flex-shrink-0" style={{ borderBottom: '2px dashed #d4d0c8' }}>
              <div className="flex items-center gap-2.5">
                <ShoppingCart size={18} weight="fill" style={{ color: colors.primary }} />
                <span className="text-[17px] font-bold" style={{ fontFamily: "'Dancing Script', cursive", color: '#3a3530' }}>Ma liste de courses</span>
                {(cartItems?.length || 0) > 0 && <span className="text-[11px] text-[#8a8580] font-medium">({cartItems.length})</span>}
              </div>
              <button onClick={() => setFloatingCartOpen(false)} className="text-[#8a8580] hover:text-[#3a3530] p-1 transition-colors"><X size={16} weight="bold" /></button>
            </div>

            <div className="px-5 py-3 flex-shrink-0" style={{ borderBottom: '1px dashed #d4d0c8' }} data-testid="delivery-progress">
              <div className="flex items-center gap-2 mb-2">
                <Truck size={14} weight="fill" className={deliveryProgress >= 100 ? 'text-green-600' : 'text-[#8a8580]'} />
                {deliveryProgress >= 100 ? (
                  <span className="text-[11px] font-bold text-green-700">Livraison gratuite !</span>
                ) : (
                  <span className="text-[11px] text-[#6a6560]">Plus que <strong style={{ color: colors.primary }}>{remaining.toFixed(2)}€</strong> pour la livraison gratuite</span>
                )}
              </div>
              <div className="w-full h-[5px] bg-[#e8e4dc] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${deliveryProgress}%`, backgroundColor: deliveryProgress >= 100 ? '#22c55e' : colors.primary }} />
              </div>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="text-[9px] text-[#a09a94] flex items-center gap-1"><CalendarCheck size={10} /> {DELIVERY_DAYS.join(', ')}</span>
                <span className="text-[9px] text-[#a09a94] flex items-center gap-1"><MapPin size={10} /> Dép. {DELIVERY_POSTCODES.join(', ')}</span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar" style={{ minHeight: 0 }}>
              <div className="px-4 py-3 space-y-1">
                {(!cartItems || cartItems.length === 0) ? (
                  <div className="text-center py-8">
                    <ShoppingCart size={32} className="mx-auto mb-2" style={{ color: '#d4d0c8' }} />
                    <p className="text-[13px]" style={{ color: '#a09a94', fontFamily: "'Dancing Script', cursive" }}>Votre liste est vide</p>
                    <p className="text-[10px] text-[#c0bab4] mt-1">Ajoutez des produits depuis la boutique</p>
                  </div>
                ) : (
                  cartItems.map((item, idx) => (
                    <div key={`${item.product_id}-${idx}`} className="flex items-center gap-2.5 py-2 group" style={{ borderBottom: '1px solid transparent' }} data-testid={`floating-cart-item-${item.product_id}`}>
                      <span className="text-[10px] w-4 text-center flex-shrink-0" style={{ color: colors.primary, fontFamily: "'Dancing Script', cursive" }}>{idx + 1}.</span>
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-[38px] h-[38px] rounded-lg object-cover flex-shrink-0 border border-[#e8e4dc]" />
                      ) : (
                        <div className="w-[38px] h-[38px] rounded-lg bg-[#ece8e0] flex-shrink-0 flex items-center justify-center text-[9px] font-bold text-[#a09a94]">{item.name?.[0]}</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <Link to={getLink(`/product/${item.slug || item.product_id}`)} onClick={() => setFloatingCartOpen(false)}
                          className="text-[11px] font-semibold text-[#3a3530] truncate block hover:underline" style={{ color: '#3a3530' }} data-testid={`cart-item-link-${item.product_id}`}>{item.name}</Link>
                        <div className="flex items-center gap-2 mt-0.5">
                          <div className="flex items-center border border-[#d4d0c8] rounded-md overflow-hidden bg-white">
                            <button onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.variation)}
                              className="w-[22px] h-[22px] flex items-center justify-center text-[#8a8580] hover:bg-[#f0ece4]">
                              <Minus size={9} weight="bold" />
                            </button>
                            <span className="w-[22px] h-[22px] flex items-center justify-center text-[10px] font-bold text-[#3a3530]">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.variation)}
                              className="w-[22px] h-[22px] flex items-center justify-center text-[#8a8580] hover:bg-[#f0ece4]">
                              <Plus size={9} weight="bold" />
                            </button>
                          </div>
                          <span className="text-[12px] font-bold" style={{ color: colors.primary }}>{(item.price * item.quantity).toFixed(2)}€</span>
                        </div>
                      </div>
                      <button onClick={() => removeItem(item.product_id, item.variation)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-[#c0bab4] hover:text-red-400 transition-all flex-shrink-0">
                        <Trash size={13} weight="bold" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              {suggested.length > 0 && (
                <div className="relative px-4 py-1">
                  <div className="border-t-2 border-dashed" style={{ borderColor: '#d4d0c8' }} />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#faf8f4] px-1 text-[10px]" style={{ color: '#a09a94' }}>&#9986;</span>
                </div>
              )}

              {suggested.length > 0 && (
                <div className="px-4 pt-1 pb-3" data-testid="suggested-products">
                  <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#a09a94' }}>On vous suggère aussi</p>
                  <div className="space-y-1.5">
                    {suggested.map(p => (
                      <div key={p.id} className="flex items-center gap-2.5 py-1 group">
                        <Link to={getLink(`/product/${p.slug}`)} className="flex items-center gap-2.5 flex-1 min-w-0">
                          {(p.featured_image || p.images?.[0]) ? (
                            <img src={p.featured_image || p.images[0]} alt={p.name} className="w-[32px] h-[32px] rounded-lg object-cover flex-shrink-0 border border-[#e8e4dc]" loading="lazy" />
                          ) : (
                            <div className="w-[32px] h-[32px] rounded-lg bg-[#ece8e0] flex-shrink-0 flex items-center justify-center text-[8px] font-bold text-[#c0bab4]">{p.name?.[0]}</div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] text-[#6a6560] truncate group-hover:text-[#3a3530]">{p.name}</p>
                            <span className="text-[10px] font-bold" style={{ color: colors.primary }}>{parseFloat(p.sale_price || p.price).toFixed(2)}€</span>
                          </div>
                        </Link>
                        <button onClick={() => addItem(p, 1, null, storeSlug)}
                          className="flex-shrink-0 w-[26px] h-[26px] rounded-md flex items-center justify-center text-white transition-all hover:scale-110"
                          style={{ backgroundColor: colors.primary }}
                          data-testid={`suggest-add-${p.id}`}>
                          <Plus size={11} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {cartItems && cartItems.length > 0 && (
              <div className="px-5 py-3.5 space-y-2.5 flex-shrink-0" style={{ borderTop: '2px dashed #d4d0c8' }}>
                {!user && (
                  <Link to={getLink('/account')} onClick={() => setFloatingCartOpen(false)}
                    className="flex items-center gap-2.5 p-2.5 bg-[#f0ece4] rounded-lg hover:bg-[#e8e4dc] transition-colors"
                    data-testid="cart-login-prompt">
                    <SignIn size={16} style={{ color: colors.primary }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold text-[#3a3530]">Connectez-vous</p>
                      <p className="text-[9px] text-[#8a8580]">pour connaître les frais de livraison</p>
                    </div>
                    <ArrowRight size={12} className="text-[#a09a94]" />
                  </Link>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-[13px]" style={{ color: '#6a6560', fontFamily: "'Dancing Script', cursive" }}>Total</span>
                  <span className="text-[20px] font-black" style={{ color: colors.primary }}>{(cartTotal || 0).toFixed(2)}€</span>
                </div>
                {taxBreakdown?.groups?.length > 0 && (
                  <div className="space-y-0.5 pt-1 border-t border-[#e8e4dc]" data-testid="floating-cart-vat">
                    {taxBreakdown.groups.map(g => (
                      <div key={g.rate} className="flex justify-between text-[10px] text-[#8a8580]">
                        <span>Dont TVA {String(g.rate).replace('.', ',')}%</span>
                        <span>{g.vat.toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                )}
                <Link to={getLink('/checkout')} onClick={() => setFloatingCartOpen(false)}
                  className="block w-full py-3 rounded-xl text-white text-center text-[14px] font-bold transition-all hover:brightness-110 hover:shadow-lg"
                  style={{ backgroundColor: colors.primary }}
                  data-testid="floating-cart-checkout">
                  Commander <ArrowRight size={16} weight="bold" className="inline ml-1" />
                </Link>
              </div>
            )}
          </div>

          <svg viewBox="0 0 370 14" className="w-full block" preserveAspectRatio="none" style={{ height: 14 }}>
            <path d="M0,0 L0,4 Q4,10 8,4 Q12,0 16,5 Q20,10 24,4 Q28,0 32,6 Q36,12 40,5 Q44,0 48,4 Q52,10 56,5 Q60,0 64,6 Q68,12 72,4 Q76,0 80,5 Q84,10 88,4 Q92,0 96,6 Q100,12 104,5 Q108,0 112,4 Q116,10 120,5 Q124,0 128,6 Q132,12 136,4 Q140,0 144,5 Q148,10 152,4 Q156,0 160,6 Q164,12 168,5 Q172,0 176,4 Q180,10 184,5 Q188,0 192,6 Q196,12 200,4 Q204,0 208,5 Q212,10 216,4 Q220,0 224,6 Q228,12 232,5 Q236,0 240,4 Q244,10 248,5 Q252,0 256,6 Q260,12 264,4 Q268,0 272,5 Q276,10 280,4 Q284,0 288,6 Q292,12 296,5 Q300,0 304,4 Q308,10 312,5 Q316,0 320,6 Q324,12 328,4 Q332,0 336,5 Q340,10 344,4 Q348,0 352,6 Q356,12 360,5 Q364,0 370,4 L370,0 Z" fill="#faf8f4" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default FloatingCart;
